import {groupBy} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import produce from 'immer';
import React, {RefObject, useEffect, useMemo, useRef, useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {FixedPlateModalStep} from 'constants/analytics-products/SamplesAnalyticsConstants';
import {STORABLE_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {DEFAULT_MEASUREMENT} from 'constants/MeasurementConstants';
import {PrimaryExperimentalRoleType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ExperimentalRole} from 'samples/experimental-roles/ExperimentalRoleConstants';
import {PlateMapType} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import {InferAnnotationParamsResponse} from 'samples/plate-maps/annotate-plate-modal/ai-assistant/AnnotationAssistantDAO';
import {inferAnnotationParamsWithAi} from 'samples/plate-maps/annotate-plate-modal/ai-assistant/AnnotationAssistantDAO';
import AnnotatePlateSettings from 'samples/plate-maps/annotate-plate-modal/AnnotatePlateSettings';
import {PlateMapDisplayAttributeState} from 'samples/plate-maps/hooks/usePlateMapDisplayAttributeState';
import {
  AssignmentGroup,
  AssignmentGroupValue,
  CONCENTRATION_ATTRIBUTE_SENTINEL,
  ConcentrationAttribute,
  PlateMap,
  PlateMapWell,
  PlateMapWellDisplayLayer,
  PlateModalAnalyticsMetadata,
  SchemaFieldAssignmentGroup,
  SchemaFieldDisplayLayer,
  SchematizedAttribute,
  WELL_ROLE_ATTRIBUTE_SENTINEL,
} from 'samples/plate-maps/PlateMapConstants';
import PlateMapPane from 'samples/plate-maps/PlateMapPane';
import {
  applyEditsToAllWells,
  applyEditToSelected,
  applySchemaTagEditToAllWells,
  applySchemaTagEditToSelected,
  getDisplayLayerAttributeKey,
  getFlattenedWellIndex,
  getInitialMinimalTag,
  isSchematizedAttribute,
} from 'samples/plate-maps/PlateMapUtil';
import {PlateMapWellSelectionSection} from 'samples/plate-maps/PlateMapWellSelectionSection';
import PreviewWellRolesButton from 'samples/plate-maps/PreviewWellRolesButton';
import PlateMapValidationBanners from 'samples/plate-maps/validation/PlateMapValidationBanners';
import {
  clearSchemaFieldErrors,
  getErrorKeyForDisplayLayerAttribute,
} from 'samples/plate-maps/validation/PlateMapValidationUtil';
import {
  FieldValuePlateMapError,
  LayerPlateMapError,
  PlateMapAnnotationError,
  PlateMapErrorType,
  ValidationErrorsByLayerAndLevel,
  WellPlateMapError,
} from 'samples/plate-maps/validation/ValidationTypes';
import {formatGridCoordinates} from 'samples/SampleUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import Badge, {BadgeColor} from 'ui-library/Badge';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHT} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {maybeLogErrorResponseToSentry} from 'util/RequestCorrelation';

export interface AnnotatePlateStepProps {
  plateMap: PlateMap;
  setPlateMap: (plateMap: PlateMap) => void;
  initialSelectedWellIndices: Set<number>;
  maxPlateViewHeight: string;
  modalRef?: RefObject<HTMLDivElement>;
  analyticsMetadata: PlateModalAnalyticsMetadata;
  displayState: PlateMapDisplayAttributeState;
  showFinalMeasurements: boolean;
  isViewOnly?: boolean;
  groupedValidationErrors?: ValidationErrorsByLayerAndLevel;
  onRemoveLayer: (selectedAttribute: SchematizedAttribute | ConcentrationAttribute) => void;
  onDismissErrors: (errorUuids: Set<string>) => void;
  /** Type of the plate map; used to render slightly different errors/warnings for designs. */
  plateMapType: PlateMapType;
}

type WellWithRole = PlateMapWell & {role: ExperimentalRole};

/**
 * Content of modal step that applies role and schema field value annotations to a plate.
 */
export default function AnnotatePlateStep({
  plateMap,
  setPlateMap,
  initialSelectedWellIndices,
  modalRef,
  analyticsMetadata,
  maxPlateViewHeight,
  displayState,
  showFinalMeasurements,
  isViewOnly,
  groupedValidationErrors,
  onRemoveLayer,
  onDismissErrors,
  plateMapType,
}: AnnotatePlateStepProps): JSX.Element {
  const previewSectionRef = useRef<HTMLDivElement>(null);
  const previewHeaderRef = useRef<HTMLDivElement>(null);
  const previewHeaderTextRef = useRef<HTMLParagraphElement>(null);

  const [selectedWellIndices, setSelectedWellIndices] = useState<Set<number>>(initialSelectedWellIndices);
  const [plateMapWithPreview, setPlateMapWithPreview] = useState<PlateMap | null>(null);
  const {assignmentGroupsPerAttribute, setAssignmentGroupsPerAttribute, selectedAttribute, displayLayer} =
    displayState;
  const [aiSuggestedPlateMap, setAISuggestedPlateMap] = useState<PlateMap | null>(null);
  const [isAIPreview, setIsAIPreview] = useState<boolean>(false);
  const [previewWellRoles, setPreviewWellRoles] = useState<boolean>(false);

  useEffect(() => {
    setPlateMapWithPreview(aiSuggestedPlateMap);
    if (aiSuggestedPlateMap != null) {
      setIsAIPreview(true);
    } else {
      setIsAIPreview(false);
    }
  }, [aiSuggestedPlateMap, setIsAIPreview, setPlateMapWithPreview]);

  function deselectWells(): void {
    setSelectedWellIndices(new Set());
  }

  function trackEventWithStep(event: analytics.AnalyticsEvent): void {
    analytics.trackEvent(event, {
      ...analyticsMetadata,
      step: FixedPlateModalStep.ANNOTATE_PLATE_STEP,
    });
  }

  function handleClickClear(): void {
    trackEventWithStep(STORABLE_ANALYTICS_ACTIONS.CLICKED_CLEAR_CELLS);

    let updatedPlateMap: PlateMap;
    if (displayLayer === PlateMapWellDisplayLayer.REAGENT) {
      // This shouldn't happen, but short-circuit if it does.
      return;
    } else if (displayLayer === PlateMapWellDisplayLayer.WELL_ROLE) {
      const newValue: Partial<PlateMapWell> = {role: null};
      if (selectedWellIndices.size === 0) {
        updatedPlateMap = applyEditsToAllWells(plateMap, newValue);
      } else {
        updatedPlateMap = applyEditToSelected(plateMap, newValue, selectedWellIndices);
      }
    } else if (displayLayer === PlateMapWellDisplayLayer.CONCENTRATION) {
      const layerId = (selectedAttribute as ConcentrationAttribute).key;
      updatedPlateMap = produce(plateMap, (draftPlateMap) => {
        draftPlateMap.wells
          .filter((_, i) => selectedWellIndices.size === 0 || selectedWellIndices.has(i))
          .forEach((draftWell) => {
            draftWell.concentrationLayers[layerId] = DEFAULT_MEASUREMENT;
          });
      });
    } else {
      const schematizedAttribute = selectedAttribute as SchematizedAttribute;
      const newValue = getInitialMinimalTag(schematizedAttribute.schemaField);
      if (selectedWellIndices.size === 0) {
        updatedPlateMap = applySchemaTagEditToAllWells(
          plateMap,
          selectedAttribute as SchematizedAttribute,
          newValue
        );
      } else {
        updatedPlateMap = applySchemaTagEditToSelected(
          plateMap,
          selectedAttribute as SchematizedAttribute,
          newValue,
          selectedWellIndices
        );
      }
    }
    setPlateMap(updatedPlateMap);
    deselectWells();
  }

  function setAssignmentGroupsForSelectedAttribute(newGroupsById: {
    [id: string]: AssignmentGroup<AssignmentGroupValue>;
  }): void {
    setAssignmentGroupsPerAttribute({
      ...assignmentGroupsPerAttribute,
      [getDisplayLayerAttributeKey(selectedAttribute)]: newGroupsById,
    });
  }

  function onChangeWellSelection(indices: Set<number>): void {
    trackEventWithStep(STORABLE_ANALYTICS_ACTIONS.CHANGED_CELL_SELECTION);
    setSelectedWellIndices(indices);
  }

  function onEditPlateMap(
    newPlateMap: PlateMap,
    additionalMetadata: {
      primaryRole?: PrimaryExperimentalRoleType;
      subrole?: string | null;
      annotatedByAi?: boolean;
      layerUsesDilution?: boolean;
    }
  ): void {
    analytics.trackEvent(STORABLE_ANALYTICS_ACTIONS.EDITED_ANNOTATIONS, {
      ...analyticsMetadata,
      ...additionalMetadata,
    });
    setPlateMap(newPlateMap);
  }

  const handleSubmitAIPrompt = async (userPrompt: string): Promise<InferAnnotationParamsResponse> => {
    const currentRoles = plateMap.wells
      .filter((well): well is WellWithRole => well.role != null)
      .reduce((acc: {[coord: string]: ExperimentalRole}, well) => {
        acc[formatGridCoordinates(well.position.row, well.position.column)] = well.role;
        return acc;
      }, {});

    let res: InferAnnotationParamsResponse;
    try {
      res = await inferAnnotationParamsWithAi({
        currentRoles,
        dimensions: {rows: plateMap.size.numRows, columns: plateMap.size.numColumns},
        userPrompt,
      });
      if (res.error == null) {
        const updatedPlateMap = produce(plateMap, (draft) => {
          draft.wells.forEach((well) => {
            const pos = formatGridCoordinates(well.position.row, well.position.column);
            well.role = res.roleAnnotations?.[pos] ?? null;
          });
          return draft;
        });
        setAISuggestedPlateMap(updatedPlateMap);
      }
      // this error is not from the llm, but from the task API endpoint
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (error) {
      maybeLogErrorResponseToSentry(error);
      // this will be shown to the user
      res = {
        error: 'An unexpected error occurred submitting your prompt',
        roleAnnotations: null,
        traceId: '',
      };
    }
    return res;
  };

  function applyAISuggestion(): void {
    trackEventWithStep(STORABLE_ANALYTICS_ACTIONS.APPLIED_ROLE_ANNOTATION_AI_SUGGESTION);
    if (aiSuggestedPlateMap == null) {
      return;
    }
    onEditPlateMap(aiSuggestedPlateMap, {annotatedByAi: true});
    setAISuggestedPlateMap(null);
  }

  function discardAISuggestion(): void {
    trackEventWithStep(STORABLE_ANALYTICS_ACTIONS.DISCARDED_ROLE_ANNOTATION_AI_SUGGESTION);
    setAISuggestedPlateMap(null);
  }

  function onResolveErrors(errors: Array<PlateMapAnnotationError>): void {
    const errorsByType = groupBy(errors, (e) => e.type);
    const hasLayerErrors = (errorsByType[PlateMapErrorType.LAYER] ?? []).length > 0;

    if (hasLayerErrors) {
      // Layer errors (e.g., duplicate concentration layer names) - remove the layer
      const layerError = errorsByType[PlateMapErrorType.LAYER][0] as LayerPlateMapError;
      assert(
        layerError.layerId === getErrorKeyForDisplayLayerAttribute(selectedAttribute),
        'Only expected to resolve errors for the current layer'
      );
      onRemoveLayer(selectedAttribute as SchematizedAttribute | ConcentrationAttribute);
    } else if (
      typeof selectedAttribute !== 'string' &&
      selectedAttribute.type === CONCENTRATION_ATTRIBUTE_SENTINEL
    ) {
      // Well errors on concentration layers (e.g., invalid measurements) - clear the values
      const wellErrors = (errorsByType[PlateMapErrorType.WELL] ?? []) as Array<WellPlateMapError>;
      const layerId = selectedAttribute.key;
      const wellErrorIndices = wellErrors.map(({coordinates}) =>
        getFlattenedWellIndex(coordinates.row, coordinates.column, plateMap.size)
      );
      const updatedPlateMap = produce(plateMap, (draft) => {
        for (const wellIndex of wellErrorIndices) {
          draft.wells[wellIndex].concentrationLayers[layerId] = DEFAULT_MEASUREMENT;
        }
      });
      setPlateMap(updatedPlateMap);
    } else {
      // Well/field errors on schema fields - clear invalid schema field values
      assert(
        isSchematizedAttribute(selectedAttribute),
        'Expected schematized attribute for schema field errors'
      );
      const updatedPlateMap = clearSchemaFieldErrors<SchemaFieldDisplayLayer>({
        plateMap,
        selectedAttribute,
        assignmentGroupsById: assignmentGroupsById as {[id: string]: SchemaFieldAssignmentGroup},
        wellErrors: (errorsByType[PlateMapErrorType.WELL] ?? []) as Array<WellPlateMapError>,
        fieldValueErrors: (errorsByType[PlateMapErrorType.FIELD_VALUE] ??
          []) as Array<FieldValuePlateMapError>,
      });
      setPlateMap(updatedPlateMap);
    }
  }

  const errorsForLayer =
    groupedValidationErrors != null
      ? (groupedValidationErrors[getErrorKeyForDisplayLayerAttribute(selectedAttribute)] ?? null)
      : null;

  const shouldDisableWellRolePreview = useMemo(
    () => plateMap.wells.every((well) => well.role == null),
    [plateMap]
  );

  const assignmentGroupsById = useMemo(
    () => assignmentGroupsPerAttribute[getDisplayLayerAttributeKey(selectedAttribute)] ?? {},
    [assignmentGroupsPerAttribute, selectedAttribute]
  );

  // Override display layer to show well roles when previewing
  const effectiveDisplayLayer = previewWellRoles ? PlateMapWellDisplayLayer.WELL_ROLE : displayLayer;
  const effectiveAttribute = previewWellRoles ? WELL_ROLE_ATTRIBUTE_SENTINEL : selectedAttribute;

  const plateMapDisplayParams = useMemo(
    () => ({
      plateMap: plateMapWithPreview ?? plateMap,
      displayLayer: effectiveDisplayLayer,
      attribute: effectiveAttribute,
      assignmentGroupsById,
      groupedValidationErrors: groupedValidationErrors ?? {},
    }),
    [
      assignmentGroupsById,
      effectiveDisplayLayer,
      effectiveAttribute,
      groupedValidationErrors,
      plateMap,
      plateMapWithPreview,
    ]
  );

  // If we are in AI preview mode, we want to show all wells as previewed. Otherwise, if there is a preview,
  // show only the selected wells as previewed.
  const previewedWellIndices = isAIPreview
    ? new Set(plateMap.wells.map((_, i) => i))
    : plateMapWithPreview != null
      ? selectedWellIndices
      : new Set<number>();

  return (
    <div className={css(styles.wrapper)} {...getTestProps({component: 'AnnotatePlateStep', element: 'root'})}>
      <div className={css(styles.previewSection)} ref={previewSectionRef}>
        <div className={css(styles.errorBanners)}>
          {errorsForLayer && (
            <PlateMapValidationBanners
              errorsByLevel={errorsForLayer}
              onResolveError={onResolveErrors}
              onDismissErrors={onDismissErrors}
            />
          )}
        </div>
        <PlateMapPane
          header={
            <div className={css(styles.previewHeader)} ref={previewHeaderRef}>
              {isAIPreview && displayLayer === PlateMapWellDisplayLayer.WELL_ROLE ? (
                <Badge color={BadgeColor.PURPLE} bold={true}>
                  <BenchlingIcon icon="sparkle" color="purple" style={{marginRight: 6}} />
                  Suggested Layout
                </Badge>
              ) : !isViewOnly ? (
                <PlateMapWellSelectionSection
                  selectedWellIndices={selectedWellIndices}
                  paragraphRef={previewHeaderTextRef}
                  setSelectedWellIndices={onChangeWellSelection}
                  totalWells={plateMap.wells.length}
                />
              ) : (
                // Empty div tag to preserve flex styling on the header
                <div />
              )}
              <div className={css(styles.headerButtons)}>
                {displayLayer === PlateMapWellDisplayLayer.CONCENTRATION && (
                  <PreviewWellRolesButton
                    previewWellRoles={previewWellRoles}
                    onToggle={() => {
                      setPreviewWellRoles(!previewWellRoles);
                    }}
                    disabled={shouldDisableWellRolePreview}
                  />
                )}
                <Button
                  icon="close"
                  size={ButtonSize.SMALL}
                  buttonStyle={ButtonStyle.TERTIARY_GRAY}
                  text={selectedWellIndices.size === 0 ? 'Clear all wells' : 'Clear wells'}
                  onClick={handleClickClear}
                  disabled={isViewOnly}
                />
              </div>
            </div>
          }
          plateMapDisplayParams={plateMapDisplayParams}
          isPlateMapViewOnly={isViewOnly ?? false}
          selectedWellIndices={selectedWellIndices}
          setSelectedWellIndices={onChangeWellSelection}
          previewedWellIndices={previewedWellIndices}
          deselectWellsOnClickRefs={[previewSectionRef, previewHeaderRef, previewHeaderTextRef]}
          modalRef={modalRef}
          maxPlateViewHeight={maxPlateViewHeight}
          showFinalMeasurements={showFinalMeasurements}
          plateMapType={plateMapType}
        />
      </div>
      <div className={css(styles.border)} />
      <AnnotatePlateSettings
        plateMap={plateMap}
        onEditPlateMap={onEditPlateMap}
        selectedWellIndices={selectedWellIndices}
        deselectWells={deselectWells}
        previewPlateMap={plateMapWithPreview}
        setPlateMapWithPreview={setPlateMapWithPreview}
        displayLayer={displayLayer}
        selectedAttribute={selectedAttribute}
        assignmentGroupsById={assignmentGroupsById}
        setAssignmentGroupsById={setAssignmentGroupsForSelectedAttribute}
        setIsAIPreview={setIsAIPreview}
        onSubmitAIPrompt={handleSubmitAIPrompt}
        onApplyAISuggestion={applyAISuggestion}
        onDiscardAISuggestion={discardAISuggestion}
        aiSuggestedPlateMap={aiSuggestedPlateMap}
        setAISuggestedPlateMap={setAISuggestedPlateMap}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
    margin: '-16px',
    padding: '16px',
  },
  errorBanners: {
    marginBottom: '16px',
  },
  previewSection: {
    flex: '1 1 auto',
    overflow: 'auto',
  },
  previewHeader: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  border: {
    flex: '0 0 0px',
    borderRight: `1px solid ${GRAY_LIGHT}`,
    margin: '-16px 16px',
  },
  aiButtons: {
    display: 'flex',
    gap: 8,
    justifyContent: 'flex-end',
  },
  headerButtons: {
    display: 'flex',
    gap: 8,
  },
});
